import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, Film } from "lucide-react";
import { useLang } from "../../context/LangContext";
import { translations, pickBilingual } from "../../lib/i18n";
import { TEST_IDS } from "../../constants/testIds";

const getEmbedUrl = (url = "") => {
  const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
  return url;
};

const getThumbnail = (item) => {
  if (item.thumbnail_url) return item.thumbnail_url;
  const yt = item.url?.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  if (yt) return `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg`;
  return null;
};

const Media = ({ media = [] }) => {
  const { lang } = useLang();
  const t = translations[lang].media;
  const [active, setActive] = useState(null);

  return (
    <section
      id="media"
      data-testid={TEST_IDS.media.root}
      className="rp-section bg-[color:var(--rp-bg-2)]"
    >
      <div className="rp-container">
        <div className="mb-14">
          <div className="rp-eyebrow mb-4">10 · {t.eyebrow}</div>
          <h2 className="rp-heading rp-headline">{t.title}</h2>
        </div>

        {media.length === 0 ? (
          <div className="border border-dashed border-white/10 py-24 text-center text-[color:var(--rp-muted)] text-sm">
            <Film className="inline-block mb-3 text-white/30" size={28} />
            <div>{t.empty}</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {media.map((item, i) => {
              const thumb = getThumbnail(item);
              return (
                <motion.button
                  key={item.id || i}
                  onClick={() => setActive(item)}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: (i % 3) * 0.05 }}
                  className="group relative aspect-video overflow-hidden rp-card p-0 text-left"
                >
                  {thumb ? (
                    <img
                      src={thumb}
                      alt={pickBilingual(item.title, lang)}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full bg-[#0f1214]" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="w-14 h-14 border border-white/40 group-hover:border-[color:var(--rp-accent)] group-hover:bg-[color:var(--rp-accent)] rounded-full flex items-center justify-center text-white transition-all">
                      <Play size={18} />
                    </span>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <div className="font-display font-semibold text-sm md:text-base">
                      {pickBilingual(item.title, lang)}
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        )}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-6"
            onClick={() => setActive(null)}
          >
            <button
              className="absolute top-6 right-6 text-white/80 hover:text-white"
              onClick={() => setActive(null)}
              aria-label="Close"
            >
              <X size={26} />
            </button>
            <div
              className="w-full max-w-5xl aspect-video bg-black"
              onClick={(e) => e.stopPropagation()}
            >
              {active.kind === "image" ? (
                <img
                  src={active.url}
                  alt={pickBilingual(active.title, lang)}
                  className="w-full h-full object-contain"
                />
              ) : (
                <iframe
                  src={getEmbedUrl(active.url)}
                  title={pickBilingual(active.title, lang)}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Media;
